import { useState } from "react";

import { Grid, Typography } from "@mui/material";
import {
  faCalendarCheck,
  faWeightHanging,
  faLocationDot,
  faShip,
  faWeightScale,
} from "@fortawesome/free-solid-svg-icons";
import { faTruckClock } from "@fortawesome/pro-solid-svg-icons";

import { Autocomplete } from "@aclymatepackages/atoms";
import { YesNoQuestion } from "@aclymatepackages/modules";

import { doesFieldExist } from "../../helpers/components/inputs";
import useCsvUploader from "./csvUploader";
import { shippingFieldOptions } from "../../helpers/components/tasks";

const buildRequiredFields = (isProviderColumnIncluded) => {
  const fields = [
    {
      label: "Date",
      value: "date",
      description: " When was the item shipped?",
      icon: faCalendarCheck,
      required: true,
    },
    {
      label: "Weight",
      value: "weight",
      description:
        "How much did the item weigh and what was the unit of weight (lbs, kgs, grams, etc...)?",
      icon: faWeightHanging,
      required: true,
    },

    {
      label: "Weight Units",
      value: "weightUnits",
      description: "(Optional) Units for the weight (lbs, kgs, metric tons)",
      icon: faWeightScale,
      required: false,
    },
    {
      label: "Service Type",
      value: "serviceType",
      description: "How was the package shipped?",
      icon: faShip,
      required: true,
    },
    {
      label: "Shipping Distance",
      value: "senderPostalCode",
      description:
        "Either the sender's/recipient's postal code or the distance traveled",
      icon: faLocationDot,
      required: false,
    },
  ];

  if (isProviderColumnIncluded) {
    return [
      ...fields,
      {
        label: "Shipping Provider",
        value: "shippingProvider",
        description: "Which shipping provider was used?",
        icon: faTruckClock,
        required: true,
      },
    ];
  }
  return fields;
};

const knownFields = [
  { header: "date", field: "date" },
  { header: "shipment date", field: "date" },
  { header: "weight", field: "weight" },
  { header: "mass", field: "weight" },
  { header: "weight units", field: "weightUnits" },
  { header: "unit of measure", field: "weightUnits" },
  { header: "sender postal code", field: "senderPostalCode" },
  { header: "origin postal code", field: "senderPostalCode" },
  { header: "from postal code", field: "senderPostalCode" },
  { header: "recipient postal code", field: "recipientPostalCode" },
  { header: "destination postal code", field: "recipientPostalCode" },
  { header: "to postal code", field: "recipientPostalCode" },
  { header: "service type", field: "serviceType" },
  { header: "shipping method", field: "serviceType" },
  { header: "carrier", field: "serviceType" },
  { header: "direction", field: "direction" },
  { header: "ghg category", field: "direction" },
  { header: "scope 3 category", field: "direction" },
];

const defaultDirectionOptions = [
  { name: "Upstream (Cat 4)", value: "upstream" },
  { name: "Downstream (Cat 9)", value: "downstream" },
];

const topShippingProviders = [
  { name: "USPS", value: "usps" },
  { name: "FedEx", value: "fedEx" },
  { name: "UPS", value: "ups" },
  { name: "DHL", value: "dhl" },
  { name: "SF Express", value: "sfExpress" },
  { name: "Royal Mail", value: "royalMail" },
  { name: "PostNL", value: "postNl" },
  { name: "Blue Dart", value: "blueDart" },
  { name: "Aramex", value: "aramex" },
  { name: "DB Schenker", value: "dbSchenker" },
  { name: "Asendia", value: "asendia" },
];

const fieldValidator = (requiredFields) => (headers) => {
  console.log("headers", headers);
  const shippingProviderField = doesFieldExist(headers, "shippingProvider");
  if (
    requiredFields.some(({ value }) => value === "shippingProvider") &&
    !shippingProviderField
  ) {
    return {
      success: false,
      message:
        'The shipping provider field is required for this CSV. Please ensure the "shippingProvider" column is included in your CSV.',
    };
  }

  const dateField = doesFieldExist(headers, "date");
  const weightField = doesFieldExist(headers, "weight");
  const weightUnitsField = doesFieldExist(headers, "weightUnits");
  const serviceTypeField = doesFieldExist(headers, "serviceType");

  if (!dateField || !weightField || !serviceTypeField || !weightUnitsField) {
    return {
      success: false,
      message:
        "Missing required date, weight, service type, or weight units fields",
    };
  }

  const senderPostalCodeField = doesFieldExist(headers, "senderPostalCode");
  const recipientPostalCodeField = doesFieldExist(
    headers,
    "recipientPostalCode"
  );
  const milesField = doesFieldExist(headers, "miles");
  const kilometersField = doesFieldExist(headers, "kilometers");

  if (
    !(senderPostalCodeField && recipientPostalCodeField) &&
    !(milesField || kilometersField)
  ) {
    return {
      success: false,
      message:
        "The required fields sender postal code AND recipient postal code, OR distance (either miles or kilometers) are required for this CSV. ",
    };
  }

  return { success: true };
};

const useShippingTransactionsCsvUploader = ({ open, setOpen }) => {
  const [fileInfo, setFileInfo] = useState({});
  const [matchedHeaders, setMatchedHeaders] = useState([]);
  const [isProviderColumnIncluded, setIsProviderColumnIncluded] =
    useState(undefined);
  const [selectedShippingProvider, setSelectedShippingProvider] =
    useState(null);
  const [selectedDefaultDirection, setSelectedDefaultDirection] =
    useState(null);

  const providerMetadata = !isProviderColumnIncluded
    ? {}
    : {
        shippingProvider: selectedShippingProvider?.name,
      };

  const additionalMetadata = {
    ...providerMetadata,
    ...(selectedDefaultDirection && {
      defaultDirection: selectedDefaultDirection.value,
    }),
  };

  const requiredFields = buildRequiredFields(isProviderColumnIncluded);

  const buildFieldOptions = () => {
    if (isProviderColumnIncluded) {
      return [
        ...shippingFieldOptions,
        {
          label: "Shipping Provider",
          value: "shippingProvider",
          type: "string",
        },
      ];
    }

    return shippingFieldOptions;
  };

  const csvUploaderSteps = useCsvUploader({
    knownFields,
    fieldOptions: buildFieldOptions(),
    open,
    setOpen,
    fileInfo,
    setFileInfo,
    matchedHeaders,
    setMatchedHeaders,
    fieldValidator: fieldValidator(requiredFields),
    docType: "shipping-transactions",
    title: "Upload your shipping transactions",
    firstStepText:
      "Upload a CSV of your shipping transactions, and we'll automatically process them.",
    requiredFields,
    additionalMetadata,
  });

  return [
    {
      label: "Select Shipping Provider",
      input: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="body2">
              Disclaimer: If you don't know the distance of your shipment, we
              currently can only support shipments originating from the United
              States or Canada postal codes.
            </Typography>
          </Grid>
          <Grid item>
            <YesNoQuestion
              question="Is there a specific shipping provider column in your CSV?"
              value={isProviderColumnIncluded}
              setValue={setIsProviderColumnIncluded}
            />
          </Grid>
          {isProviderColumnIncluded === false && (
            <Grid item>
              <Autocomplete
                value={selectedShippingProvider}
                setValue={(selectedProvider) =>
                  typeof selectedProvider === "string"
                    ? setSelectedShippingProvider({ name: selectedProvider })
                    : setSelectedShippingProvider(selectedProvider)
                }
                availableOptions={topShippingProviders}
                label="Enter your shipping provider"
                size="small"
                freeSolo
                autoSelect
              />
            </Grid>
          )}
          <Grid item>
            <Typography variant="body2" color="textSecondary">
              Default direction for rows without a Direction value (Upstream =
              Scope 3 Category 4, Downstream = Category 9):
            </Typography>
          </Grid>
          <Grid item>
            <Autocomplete
              value={selectedDefaultDirection}
              setValue={setSelectedDefaultDirection}
              availableOptions={defaultDirectionOptions}
              label="Default direction (optional)"
              size="small"
            />
          </Grid>
        </Grid>
      ),
      buttonDisabled: !isProviderColumnIncluded && !selectedShippingProvider,
    },
    ...csvUploaderSteps,
  ];
};

export default useShippingTransactionsCsvUploader;
